import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  IconBell, IconBriefcase, IconBuildingBank, IconChecklist, IconChevronDown, IconChevronLeft, IconChevronRight, IconCopy, IconDots, IconEye, IconHistory, IconId,
  IconInfoCircle, IconKey, IconLink, IconLock, IconMessageCircle, IconPencil, IconPhone, IconPlus, IconRefresh, IconScale, IconSteeringWheel, IconUsers, IconUserShield,
  type Icon,
} from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  personById, profileAudit, profilePeople, profileSections, profileSums, PROFILE_PEOPLE, type ProfilePerson, type ProfileSec,
} from './profile';
import type { SeatKind } from './types';
import s from './profile.module.css';
import { useT } from '@hana/i18n';
import './staffprofile.i18n';

// Mint-StaffProfile (1536 / 1920), tab Hồ sơ (design-specs/Mint-StaffProfile.md). Chỉ đọc; nút ghi báo toast.
// Ô nhạy cảm luôn che ••••••; không lương, không CCCD / số tài khoản. Các tab khác chưa mở ở bản này.

type Tab = 'info' | 'docs' | 'pay' | 'dec' | 'leave' | 'fin' | 'jd';

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const KIND_TONE: Record<SeatKind, string> = { Primary: s.tMute ?? '', Concurrent: s.tWarn ?? '', Acting: s.tPri ?? '' };
const KIND_SUFFIX: Record<SeatKind, string> = { Primary: '', Concurrent: ' · kiêm', Acting: ' · quyền' };
const SEC_ICON: Record<ProfileSec['key'], Icon> = { id: IconId, job: IconBriefcase, contact: IconPhone, bank: IconBuildingBank, drv: IconSteeringWheel, acc: IconKey };
const ini = (u: string) => u.slice(0, 2).toUpperCase();

function Card({ title, tip, icon, extra, actions, open, onToggle, toggleLabel, children }: {
  title: string; tip: string; icon: ReactNode; extra?: ReactNode; actions?: ReactNode; open: boolean; onToggle: () => void; toggleLabel?: string; children: ReactNode;
}) {
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.hd}>
        <span className={s.hdIc} aria-hidden="true">{icon}</span>
        <h2>{title}</h2>
        <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
        {extra}
        <div className={s.tools}>
          {actions}
          <button type="button" className={s.tog} aria-expanded={open} aria-label={toggleLabel ?? `Mở hoặc gập ${title}`} onClick={onToggle}>
            <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />
          </button>
        </div>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

function FieldCard({ sec, open, onToggle, onWrite }: { sec: ProfileSec; open: boolean; onToggle: () => void; onWrite: (m: string) => void }) {
  const Ico = SEC_ICON[sec.key];
  const reveal = 'Hiện 30 giây · ghi nhật ký';
  return (
    <Card
      title={sec.title}
      tip={sec.tip}
      icon={<Ico size={18} stroke={1.9} />}
      extra={sec.note ? <span className={`${s.note} ${sec.tone === 'pri' ? s.tPri : s.tMute}`}>{sec.note}</span> : undefined}
      open={open}
      onToggle={onToggle}
      toggleLabel="Mở hoặc gập mục"
      actions={
        <>
          {sec.sens && <button type="button" className={s.ico} aria-label={reveal} title={reveal} onClick={() => onWrite('Xem dữ liệu nhạy cảm')}><IconEye size={16} stroke={1.9} aria-hidden="true" /></button>}
          <button type="button" className={`${s.ico} ${s.icoPri}`} aria-label="Sửa" title="Sửa" onClick={() => onWrite('Sửa hồ sơ')}><IconPencil size={16} stroke={1.9} aria-hidden="true" /></button>
        </>
      }
    >
      <div className={s.fields}>
        {sec.fields.map(([k, v]) => (
          <div key={k} className={s.field}>
            <div className={s.fk}>{k}</div>
            {v === null
              ? <div className={`${s.fv} ${s.masked}`}><IconLock size={14} stroke={1.9} aria-hidden="true" />••••••</div>
              : <div className={s.fv}>{v}</div>}
          </div>
        ))}
      </div>
    </Card>
  );
}

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'info', label: 'Hồ sơ' }, { key: 'docs', label: 'Tài liệu' }, { key: 'pay', label: 'Lương & hợp đồng' }, { key: 'dec', label: 'Quyết định' },
  { key: 'leave', label: 'Nghỉ phép' }, { key: 'fin', label: 'Tài chính cá nhân' }, { key: 'jd', label: 'Mô tả công việc' },
];

export function StaffProfile() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { staffId } = useParams({ strict: false }) as { staffId?: string };
  const forced = readState();
  const p: ProfilePerson = personById(staffId);
  const [tab, setTab] = useState<Tab>('info');
  const [openC, setOpenC] = useState<Record<string, boolean>>({ sum: true, id: true, job: true, contact: true, drv: p.driver, act: true, info: true, todo: true, ppl: true, links: true });
  const [done, setDone] = useState(false);
  const tog = (k: string) => () => setOpenC((o) => ({ ...o, [k]: !o[k] }));
  const noWrite = (m?: string) => toast({ msg: m ? `${m} · chưa mở ghi ở bản này` : 'Chưa mở ghi ở bản này' });
  const back = () => void navigate({ to: '/ezy/hr' });

  const secs = profileSections(p);
  const sums = profileSums(p);
  const missN = p.miss.length;
  const people = profilePeople(p);
  const tabItems = TABS.map((tb) => (tb.key === 'docs' && missN ? { ...tb, n: missN } : tb));
  const openN = done ? 0 : 1;
  const left = secs.filter((x) => x.key !== 'job');
  const job = secs.find((x) => x.key === 'job')!;
  const state: ScreenState = forced ?? 'ready';

  return (
    <Page kind="detail">
      <PageHeader
        title={p.name}
        meta={<Chip label={t('sp.status')}>{t('sp.working')}</Chip>}
        lead={<button type="button" className={s.back} aria-label={t('sp.backList')} title={t('sp.backList')} onClick={back}><IconChevronLeft size={20} stroke={2} aria-hidden="true" /></button>}
        actions={
          <>
            <HeaderTool label="Thông báo · 4 mới" icon={IconBell} n={4} />
            <HeaderTool label="Thêm thao tác" icon={IconDots} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => noWrite(t('sp.newStaff'))}>{t('sp.newStaff')}</Button>
          </>
        }
      />
      <div className={s.tabs}>
        <Segmented label="Mục" value={tab} onChange={(k: Tab) => (k === 'info' ? setTab(k) : toast({ msg: 'Tab này chưa mở ở bản này' }))} items={tabItems} />
      </div>
      <StateView state={state} skeleton={<Skeleton rows={10} />} action={undefined}>
        <div className={s.grid}>
          <div className={s.col}>
            <Card title="Tóm tắt" tip="Người đang xem hồ sơ, ghế đang giữ (Primary / kiêm / quyền theo TblOrgAssign) và các số nhanh. Đổi người ở ô Hồ sơ của." icon={<IconId size={18} stroke={1.9} />} open={!!openC.sum} onToggle={tog('sum')}>
              <div className={s.me}>
                <span className={s.avL} aria-hidden="true">{ini(p.id)}</span>
                <div className={s.who}>
                  <div><b>{p.name}</b> <span>{p.id} · {p.pos}</span></div>
                  <div className={s.chips}>
                    {p.seats.map(([code, kind]) => <span key={code} className={`${s.chip} ${KIND_TONE[kind]}`}>{code}{KIND_SUFFIX[kind]}</span>)}
                  </div>
                </div>
                <label className={s.pick}>
                  <span>{t('sp.profileOf')}</span>
                  <select aria-label="Chọn nhân sự" value={p.id} onChange={(e) => void navigate({ to: '/ezy/hr/profile/$staffId', params: { staffId: e.target.value } })}>
                    {PROFILE_PEOPLE.map((o) => <option key={o.id} value={o.id}>{o.id} · {o.title}</option>)}
                  </select>
                </label>
              </div>
              <div className={s.sums}>
                {sums.map((x) => (
                  <button key={x.k} type="button" className={s.sum} title={`${x.tip} · mở tab ${x.tab}`} onClick={() => toast({ msg: 'Tab này chưa mở ở bản này' })}>
                    <span>{x.k}</span>
                    <b className={x.warn ? s.warnV : undefined}>{x.v}</b>
                  </button>
                ))}
              </div>
            </Card>
            {left.map((sec) => <FieldCard key={sec.key} sec={sec} open={!!openC[sec.key]} onToggle={tog(sec.key)} onWrite={noWrite} />)}
            <Card title="Hoạt động" tip="Nhật ký hồ sơ: sửa mục, xem dữ liệu nhạy cảm, tải tài liệu, áp quyết định, đồng bộ M365." icon={<IconHistory size={18} stroke={1.9} />} extra={<span className={s.cnt}>{profileAudit.length} mục</span>} open={!!openC.act} onToggle={tog('act')}>
              {profileAudit.map((a) => (
                <div key={a.k} className={s.actRow}>
                  <span className={s.avS} aria-hidden="true">{ini(a.who)}</span>
                  <span className={s.actTx}><span title={a.k}>{a.k}</span><span>{a.who} · {a.when}</span></span>
                  <span className={s.kind}>{a.kind}</span>
                </div>
              ))}
            </Card>
          </div>
          <div className={s.col}>
            <FieldCard sec={job} open={!!openC.job} onToggle={tog('job')} onWrite={noWrite} />
            <Card title="Ghế & tài khoản" tip="Ghế đang giữ theo TblOrgAssign (Primary / kiêm / quyền), trạng thái làm việc và UserName đăng nhập. Giao ghế ở Nhân sự › Cơ cấu & ghế." icon={<IconUsers size={18} stroke={1.9} />} open={!!openC.info} onToggle={tog('info')}>
              {p.seats.map(([code, kind], i) => (
                <div key={code} className={s.info}>
                  <span>{i ? (kind === 'Acting' ? 'Ghế quyền' : 'Ghế kiêm') : 'Ghế chính'}</span>
                  <span className={i ? s.warnV : undefined}>{code} · {kind}</span>
                </div>
              ))}
              <div className={s.info}><span>{t('sp.status')}</span><span className={s.okV}>{t('sp.working')}</span></div>
              <div className={s.info}>
                <span>UserName</span><span>{p.id}</span>
                <button type="button" className={s.copy} aria-label="Chép UserName" title="Chép" onClick={() => { void navigator.clipboard?.writeText(p.id); toast({ msg: 'Đã chép UserName' }); }}><IconCopy size={16} stroke={1.9} aria-hidden="true" /></button>
              </div>
            </Card>
            <Card title="Việc cần làm" tip="Việc hồ sơ sinh từ dữ liệu: tài liệu thiếu, HĐ sắp hết hạn, chứng chỉ hết hạn, đồng bộ M365. Tick để đánh dấu xong." icon={<IconChecklist size={18} stroke={1.9} />} extra={openN ? <span className={s.badge}>{openN}</span> : undefined} open={!!openC.todo} onToggle={tog('todo')}>
              <div className={s.todo} style={done ? { opacity: 0.55 } : undefined}>
                <button type="button" className={s.chk} role="checkbox" aria-checked={done} aria-label={t('sp.todoDone')} onClick={() => setDone((d) => !d)}><span>{done ? '✓' : ''}</span></button>
                <span className={s.todoTx}>
                  <span title={t('sp.todoSync')} style={done ? { textDecoration: 'line-through' } : undefined}>{t('sp.todoSync')}</span>
                  <span><span className={s.avT} aria-hidden="true">DA</span><span>DAVID ·</span><span>{t('sp.thisWeek')}</span></span>
                </span>
                {!done && <button type="button" className={s.act} onClick={() => noWrite(t('sp.syncShort'))}><IconRefresh size={14} stroke={1.9} aria-hidden="true" />{t('sp.sync')}</button>}
              </div>
              {done && <div className={s.none}>{t('sp.noTodo')}</div>}
            </Card>
            <Card title="Người phụ trách" tip="Avatar lấy từ TblStaff.ImageProfile (HanaAvatar), chưa có ảnh thì hiện chữ cái. Người duyệt hiện theo ghế (TblOrgAssign)." icon={<IconUserShield size={18} stroke={1.9} />} open={!!openC.ppl} onToggle={tog('ppl')}>
              {people.map((u) => (
                <div key={u.u} className={s.ppl}>
                  <span className={s.avM} aria-hidden="true">{ini(u.u)}</span>
                  <span className={s.pplTx}><b>{u.u}</b><span>{u.role}</span></span>
                  {u.hei && <a className={s.hei} href="/hei/" target="_blank" rel="noopener" aria-label={`Nhắn ${u.u} trong HANA Hei`} title="Nhắn trong HANA Hei"><IconMessageCircle size={18} stroke={1.9} aria-hidden="true" /></a>}
                </div>
              ))}
            </Card>
            <Card title="Liên kết" tip="Lối sang màn liên quan. Mở trong cùng HANA Ezy, giữ ngữ cảnh bản ghi." icon={<IconLink size={18} stroke={1.9} />} open={!!openC.links} onToggle={tog('links')}>
              <button type="button" className={s.link} onClick={() => noWrite(t('sp.mkDecision'))}>
                <IconScale size={18} stroke={1.9} aria-hidden="true" />
                <span><span>{t('sp.mkDecision')}</span><span>{t('sp.mkDecisionSub')}</span></span>
                <IconChevronRight size={16} aria-hidden="true" />
              </button>
              <button type="button" className={s.link} onClick={back}>
                <IconUsers size={18} stroke={1.9} aria-hidden="true" />
                <span><span>{t('sp.staffList')}</span><span>{t('sp.toList')}</span></span>
                <IconChevronRight size={16} aria-hidden="true" />
              </button>
            </Card>
          </div>
        </div>
      </StateView>
    </Page>
  );
}
